import React, { useEffect, useMemo, useState } from "react";

import {
  FiBox,
  FiDollarSign,
  FiFileText,
  FiImage,
  FiPackage,
  FiSave,
  FiSettings,
  FiShoppingBag,
  FiTag,
  FiTruck,
  FiUpload,
  FiX,
  FiTrash2
} from "react-icons/fi";

import API from "../../../../../../utils/apiCalling";
import { config } from "../../../../../../utils/apiUrl";
import useSwal from "../../../../../../hooks/useSwal";

import DynamicAttributes from "./DynamicAttributes";
import CategorySelect from "../shared/CategorySelect";

import "./ProductManagement.css";

/* =========================================================
   CONSTANTS
========================================================= */

const MAX_PRODUCT_IMAGES = 5;

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;

const ALLOWED_IMAGE_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
];

const PRODUCT_UNITS = [
  {
    value: "piece",
    label: "Piece",
  },
  {
    value: "pair",
    label: "Pair",
  },
  {
    value: "box",
    label: "Box",
  },
  {
    value: "packet",
    label: "Packet",
  },
  {
    value: "kg",
    label: "Kilogram",
  },
  {
    value: "gram",
    label: "Gram",
  },
  {
    value: "liter",
    label: "Liter",
  },
  {
    value: "meter",
    label: "Meter",
  },
  {
    value: "set",
    label: "Set",
  },
  {
    value: "service",
    label: "Service",
  },
];

const PRODUCT_TYPES = [
  {
    value: "physical",
    label: "Physical",
  },
  {
    value: "digital",
    label: "Digital",
  },
  {
    value: "service",
    label: "Service",
  },
];

const PRODUCT_CONDITIONS = [
  {
    value: "new",
    label: "New",
  },
  {
    value: "used",
    label: "Used",
  },
  {
    value: "refurbished",
    label: "Refurbished",
  },
  {
    value: "open-box",
    label: "Open Box",
  },
];

const PRODUCT_STATUSES = [
  {
    value: "active",
    label: "Active",
  },
  {
    value: "inactive",
    label: "Inactive",
  },
  {
    value: "draft",
    label: "Draft",
  },
];

/* =========================================================
   INITIAL FORM
========================================================= */

const INITIAL_FORM = {
  /* =======================================================
     BASIC
  ======================================================= */

  name: "",

  slug: "",

  sku: "",

  barcode: "",

  /* =======================================================
     CATEGORY
  ======================================================= */

  category: "",

  subCategory: "",

  /* =======================================================
     PRODUCT INFO
  ======================================================= */

  brand: "",

  modelNumber: "",

  condition: "new",

  productType: "physical",

  /* =======================================================
     DESCRIPTION
  ======================================================= */

  shortDescription: "",

  description: "",

  /* =======================================================
     PRICING
  ======================================================= */

  purchasePrice: "",

  sellingPrice: "",

  discountedPrice: "",

  taxPercentage: "0",

  /* =======================================================
     INVENTORY
  ======================================================= */

  stockQty: "0",

  reservedQty: "0",

  lowStockThreshold: "5",

  unit: "piece",

  /* =======================================================
     GENERAL SPECIFICATIONS
  ======================================================= */

  color: "",

  size: "",

  warranty: "",

  /* =======================================================
     DYNAMIC ATTRIBUTES
  ======================================================= */

  attributes: [],

  /* =======================================================
     SUPPLIER
  ======================================================= */

  supplierName: "",

  supplierMobile: "",

  supplierInvoiceNumber: "",

  /* =======================================================
     TAGS
  ======================================================= */

  tagsText: "",

  /* =======================================================
     SEO
  ======================================================= */

  metaTitle: "",

  metaDescription: "",

  metaKeywordsText: "",

  /* =======================================================
     STATUS
  ======================================================= */

  status: "active",

  isFeatured: false,

  isVerified: false,

  dealEnd: "",
};

/* =========================================================
   HELPERS
========================================================= */

const normalizeId = (value) => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  if (typeof value === "object") {
    return value._id || value.id || "";
  }

  return String(value);
};

/* =========================================================
   SLUG
========================================================= */

const createSlug = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

/* =========================================================
   ATTRIBUTE KEY
========================================================= */

const createAttributeKey = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

/* =========================================================
   CSV STRING -> ARRAY
========================================================= */

const commaStringToArray = (value) => {
  return [
    ...new Set(
      String(value || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean),
    ),
  ];
};

/* =========================================================
   ARRAY -> CSV STRING
========================================================= */

const arrayToCommaString = (value) => {
  return Array.isArray(value) ? value.filter(Boolean).join(", ") : "";
};

/* =========================================================
   IMAGE URL
========================================================= */

const getImageUrl = (value) => {
  if (!value) {
    return "";
  }

  const image = String(value);

  if (
    image.startsWith("blob:") ||
    image.startsWith("data:") ||
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }

  const baseUrl = String(process.env.REACT_APP_API_URL || "").replace(
    /\/+$/,
    "",
  );

  const cleanPath = image.replace(/^\/+/, "");

  if (!baseUrl) {
    return `/${cleanPath}`;
  }

  return `${baseUrl}/${cleanPath}`;
};

/* =========================================================
   DATE -> INPUT DATE
========================================================= */

const toDateInputValue = (value) => {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();

  const month = String(date.getMonth() + 1).padStart(2, "0");

  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/* =========================================================
   PRODUCT ATTRIBUTES -> FORM ATTRIBUTES
========================================================= */

const normalizeProductAttributes = (attributes) => {
  if (!Array.isArray(attributes)) {
    return [];
  }

  return attributes.map((attribute) => ({
    attributeId: normalizeId(attribute?.attributeId) || null,

    key: String(attribute?.key || createAttributeKey(attribute?.name)),

    name: String(attribute?.name || ""),

    value: attribute?.value ?? "",

    unit: String(attribute?.unit || ""),
  }));
};

/* =========================================================
   COMPONENT
========================================================= */

const AddEditProduct = ({
  open,

  onClose,

  onSuccess,

  product,
}) => {
  const api = useMemo(() => new API(), []);

  const swal = useSwal();

  /* =======================================================
     MODE
  ======================================================= */

  const isEdit = Boolean(product?._id);

  /* =======================================================
     FORM
  ======================================================= */

  const [form, setForm] = useState({
    ...INITIAL_FORM,
  });

  /* =======================================================
     IMAGE STATE
  ======================================================= */

  const [thumbnail, setThumbnail] = useState(null);

  const [thumbnailPreview, setThumbnailPreview] = useState("");

  /*
   * Only NEW files.
   */

  const [images, setImages] = useState([]);

  /*
   * Contains:
   *
   * existing server paths
   *
   * +
   *
   * blob preview URLs
   */

  const [imagePreview, setImagePreview] = useState([]);

  /* =======================================================
     UI STATE
  ======================================================= */

  const [loading, setLoading] = useState(false);

  const [errors, setErrors] = useState({});

  const [slugTouched, setSlugTouched] = useState(false);

  /* =======================================================
     CLEAN BLOB URLS
  ======================================================= */

  const revokeBlobUrl = (value) => {
    if (typeof value === "string" && value.startsWith("blob:")) {
      URL.revokeObjectURL(value);
    }
  };

  /* =======================================================
     RESET PREVIEWS
  ======================================================= */

  const cleanupPreviews = () => {
    revokeBlobUrl(thumbnailPreview);

    imagePreview.forEach((preview) => {
      revokeBlobUrl(preview);
    });
  };

  /* =======================================================
     FORM INITIALIZATION
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    /* ===================================================
         EDIT
      =================================================== */

    if (product?._id) {
      const supplier = product?.supplier || {};

      setForm({
        ...INITIAL_FORM,

        name: product?.name || "",

        slug: product?.slug || "",

        sku: product?.sku || "",

        barcode: product?.barcode || "",

        category: normalizeId(product?.category),

        subCategory: normalizeId(product?.subCategory),

        brand: product?.brand || "",

        modelNumber: product?.modelNumber || "",

        condition: product?.condition || "new",

        productType: product?.productType || "physical",

        shortDescription: product?.shortDescription || "",

        description: product?.description || "",

        purchasePrice: product?.purchasePrice ?? "",

        sellingPrice: product?.sellingPrice ?? "",

        discountedPrice: product?.discountedPrice ?? "",

        taxPercentage: product?.taxPercentage ?? "0",

        stockQty: product?.stockQty ?? "0",

        reservedQty: product?.reservedQty ?? "0",

        lowStockThreshold: product?.lowStockThreshold ?? "5",

        unit: product?.unit || "piece",

        color: product?.color || "",

        size: product?.size || "",

        warranty: product?.warranty || "",

        attributes: normalizeProductAttributes(product?.attributes),

        supplierName: supplier?.name || "",

        supplierMobile: supplier?.mobile || "",

        supplierInvoiceNumber: supplier?.invoiceNumber || "",

        tagsText: arrayToCommaString(product?.tags),

        metaTitle: product?.metaTitle || "",

        metaDescription: product?.metaDescription || "",

        metaKeywordsText: arrayToCommaString(product?.metaKeywords),

        status: product?.status || "active",

        isFeatured: Boolean(product?.isFeatured),

        isVerified: Boolean(product?.isVerified),

        dealEnd: toDateInputValue(product?.dealEnd),
      });

      setThumbnail(null);

      setThumbnailPreview(product?.thumbnail || "");

      setImages([]);

      setImagePreview(Array.isArray(product?.images) ? product.images : []);

      setSlugTouched(true);

      setErrors({});

      return;
    }

    /* ===================================================
         CREATE
      =================================================== */

    setForm({
      ...INITIAL_FORM,

      sku: generateSKU(),

      barcode: generateBarcode(),
    });

    setThumbnail(null);

    setThumbnailPreview("");

    setImages([]);

    setImagePreview([]);

    setSlugTouched(false);

    setErrors({});
  }, [open, product]);

  /* =======================================================
     CLEANUP ON UNMOUNT
  ======================================================= */

  useEffect(() => {
    return () => {
      cleanupPreviews();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* =======================================================
     GENERATE SKU
  ======================================================= */

  function generateSKU() {
    const timestamp = Date.now().toString().slice(-6);

    const random = Math.floor(100 + Math.random() * 900);

    return `CW-${timestamp}-${random}`;
  }

  /* =======================================================
     GENERATE BARCODE
  ======================================================= */

  function generateBarcode() {
    const time = Date.now().toString();

    const random = Math.floor(100 + Math.random() * 900);

    return `${time}${random}`;
  }

  /* =======================================================
     INPUT CHANGE
  ======================================================= */

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;

    const nextValue = type === "checkbox" ? checked : value;

    setForm((previous) => {
      const next = {
        ...previous,

        [name]: nextValue,
      };

      /*
       * Automatically generate
       * slug while it hasn't been
       * manually edited.
       */

      if (name === "name" && !slugTouched) {
        next.slug = createSlug(value);
      }

      return next;
    });

    if (name === "slug") {
      setSlugTouched(true);
    }

    if (errors[name]) {
      setErrors((previous) => ({
        ...previous,

        [name]: "",
      }));
    }
  };

  /* =======================================================
     CATEGORY
  ======================================================= */

  const handleCategoryChange = (category) => {
    setForm((previous) => ({
      ...previous,

      category,

      subCategory: "",
    }));

    setErrors((previous) => ({
      ...previous,

      category: "",

      subCategory: "",
    }));
  };

  const handleSubCategoryChange = (subCategory) => {
    setForm((previous) => ({
      ...previous,

      subCategory,
    }));

    setErrors((previous) => ({
      ...previous,

      subCategory: "",
    }));
  };

  /* =======================================================
     THUMBNAIL
  ======================================================= */

  const handleThumbnail = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      swal.warning({
        icon: "warning",

        title: "Invalid Image",

        text: "Please select JPG, PNG or WebP image.",
      });

      event.target.value = "";

      return;
    }

    if (file.size > MAX_IMAGE_SIZE) {
      swal.warning({
        icon: "warning",

        title: "Image Too Large",

        text: "Thumbnail must be 5 MB or smaller.",
      });

      event.target.value = "";

      return;
    }

    revokeBlobUrl(thumbnailPreview);

    const preview = URL.createObjectURL(file);

    setThumbnail(file);

    setThumbnailPreview(preview);

    setErrors((previous) => ({
      ...previous,

      thumbnail: "",
    }));

    event.target.value = "";
  };

  /* =======================================================
     REMOVE THUMBNAIL
  ======================================================= */

  const removeThumbnail = () => {
    revokeBlobUrl(thumbnailPreview);

    setThumbnail(null);

    setThumbnailPreview("");
  };

  /* =======================================================
     MULTIPLE IMAGES
  ======================================================= */

  const handleImages = (event) => {
    const selectedFiles = Array.from(event.target.files || []);

    if (selectedFiles.length === 0) {
      return;
    }

    const validFiles = selectedFiles.filter(
      (file) =>
        ALLOWED_IMAGE_TYPES.includes(file.type) && file.size <= MAX_IMAGE_SIZE,
    );

    if (validFiles.length !== selectedFiles.length) {
      swal.warning({
        icon: "warning",

        title: "Some Images Were Skipped",

        text: "Only JPG, PNG and WebP images up to 5 MB are allowed.",
      });
    }

    const availableSlots = Math.max(
      MAX_PRODUCT_IMAGES - imagePreview.length,

      0,
    );

    if (availableSlots === 0) {
      swal.warning({
        icon: "warning",

        title: "Image Limit",

        text: `Maximum ${MAX_PRODUCT_IMAGES} product images are allowed.`,
      });

      event.target.value = "";

      return;
    }

    const filesToAdd = validFiles.slice(0, availableSlots);

    if (validFiles.length > availableSlots) {
      swal.warning({
        icon: "warning",

        title: "Image Limit",

        text: `Maximum ${MAX_PRODUCT_IMAGES} product images are allowed.`,
      });
    }

    const previews = filesToAdd.map((file) => URL.createObjectURL(file));

    setImages((previous) => [...previous, ...filesToAdd]);

    setImagePreview((previous) => [...previous, ...previews]);

    event.target.value = "";
  };

  /* =======================================================
     REMOVE GALLERY IMAGE
  ======================================================= */

  const removeImage = (index) => {
    const selectedPreview = imagePreview[index];

    /*
     * Existing server image:
     * remove from preview only.
     *
     * Later existingImages tells backend
     * which server images to keep.
     */

    if (
      typeof selectedPreview === "string" &&
      !selectedPreview.startsWith("blob:")
    ) {
      setImagePreview((previous) =>
        previous.filter((_, previewIndex) => previewIndex !== index),
      );

      return;
    }

    /*
     * New image:
     * remove its file and blob.
     */

    const blobIndexes = imagePreview
      .slice(0, index)
      .filter((preview) => String(preview).startsWith("blob:")).length;

    revokeBlobUrl(selectedPreview);

    setImagePreview((previous) =>
      previous.filter((_, previewIndex) => previewIndex !== index),
    );

    setImages((previous) =>
      previous.filter((_, imageIndex) => imageIndex !== blobIndexes),
    );
  };

  /* =======================================================
     DYNAMIC ATTRIBUTES
  ======================================================= */

  const handleAttributesChange = (nextAttributes) => {
    setForm((previous) => ({
      ...previous,

      attributes: Array.isArray(nextAttributes) ? nextAttributes : [],
    }));
  };

  /* =======================================================
     NORMALIZE ATTRIBUTES FOR API
  ======================================================= */

  const getNormalizedAttributes = () => {
    return (Array.isArray(form.attributes) ? form.attributes : [])
      .map((attribute) => {
        const name = String(attribute?.name || "").trim();

        const value = attribute?.value;

        const normalizedValue =
          typeof value === "string" ? value.trim() : value;

        if (
          !name ||
          normalizedValue === "" ||
          normalizedValue === null ||
          normalizedValue === undefined
        ) {
          return null;
        }

        return {
          attributeId: normalizeId(attribute?.attributeId) || null,

          key: createAttributeKey(attribute?.key || name),

          name,

          value: normalizedValue,

          unit: String(attribute?.unit || "").trim(),
        };
      })
      .filter(Boolean);
  };

  /* =======================================================
     VALIDATE
  ======================================================= */

  const validate = () => {
    const nextErrors = {};

    /* ===================================================
         NAME
      =================================================== */

    if (!String(form.name || "").trim()) {
      nextErrors.name = "Product name is required.";
    }

    /* ===================================================
         SLUG
      =================================================== */

    if (!createSlug(form.slug || form.name)) {
      nextErrors.slug = "Product slug is required.";
    }

    /* ===================================================
         SKU
      =================================================== */

    if (!String(form.sku || "").trim()) {
      nextErrors.sku = "SKU is required.";
    }

    /* ===================================================
         CATEGORY
      =================================================== */

    if (!form.category) {
      nextErrors.category = "Please select a category.";
    }

    /* ===================================================
         PRICE
      =================================================== */

    const purchasePrice = Number(form.purchasePrice || 0);

    const sellingPrice = Number(form.sellingPrice);

    const discountedPrice = Number(form.discountedPrice || 0);

    if (Number.isNaN(purchasePrice) || purchasePrice < 0) {
      nextErrors.purchasePrice = "Purchase price cannot be negative.";
    }

    if (
      form.sellingPrice === "" ||
      Number.isNaN(sellingPrice) ||
      sellingPrice < 0
    ) {
      nextErrors.sellingPrice = "Valid selling price is required.";
    }

    if (Number.isNaN(discountedPrice) || discountedPrice < 0) {
      nextErrors.discountedPrice = "Discounted price cannot be negative.";
    }

    if (discountedPrice > 0 && discountedPrice > sellingPrice) {
      nextErrors.discountedPrice =
        "Discounted price cannot exceed selling price.";
    }

    /* ===================================================
         TAX
      =================================================== */

    const tax = Number(form.taxPercentage || 0);

    if (Number.isNaN(tax) || tax < 0 || tax > 100) {
      nextErrors.taxPercentage = "Tax percentage must be between 0 and 100.";
    }

    /* ===================================================
         STOCK

         Only CREATE validates opening stock.

         UPDATE does not directly modify stockQty.
      =================================================== */

    if (!isEdit) {
      const stockQty = Number(form.stockQty || 0);

      if (!Number.isInteger(stockQty) || stockQty < 0) {
        nextErrors.stockQty =
          "Opening stock must be a whole number of 0 or greater.";
      }
    }

    /* ===================================================
         RESERVED
      =================================================== */

    const reservedQty = Number(form.reservedQty || 0);

    if (!Number.isInteger(reservedQty) || reservedQty < 0) {
      nextErrors.reservedQty = "Reserved quantity must be 0 or greater.";
    }

    /* ===================================================
         LOW STOCK
      =================================================== */

    const threshold = Number(form.lowStockThreshold || 0);

    if (!Number.isInteger(threshold) || threshold < 0) {
      nextErrors.lowStockThreshold =
        "Low-stock threshold must be 0 or greater.";
    }

    /* ===================================================
         THUMBNAIL

         Keeping thumbnail required because your
         existing product UX expects a main image.
      =================================================== */

    if (!thumbnailPreview && !thumbnail) {
      nextErrors.thumbnail = "Product thumbnail is required.";
    }

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };

  /* =======================================================
     BUILD FORMDATA
  ======================================================= */

  const buildFormData = () => {
    const formData = new FormData();

    const appendText = (key, value) => {
      formData.append(key, String(value ?? "").trim());
    };

    /* ===================================================
         ID
      =================================================== */

    if (isEdit) {
      appendText("id", product._id);
    }

    /* ===================================================
         BASIC
      =================================================== */

    appendText("name", form.name);

    appendText("slug", createSlug(form.slug || form.name));

    appendText("sku", String(form.sku || "").toUpperCase());

    appendText("barcode", form.barcode);

    /* ===================================================
         CATEGORY
      =================================================== */

    appendText("category", normalizeId(form.category));

    appendText("subCategory", normalizeId(form.subCategory));

    /* ===================================================
         INFO
      =================================================== */

    appendText("brand", form.brand);

    appendText("modelNumber", form.modelNumber);

    appendText("condition", form.condition);

    appendText("productType", form.productType);

    /* ===================================================
         DESCRIPTION
      =================================================== */

    appendText("shortDescription", form.shortDescription);

    appendText("description", form.description);

    /* ===================================================
         PRICING
      =================================================== */

    appendText("purchasePrice", form.purchasePrice || 0);

    appendText("sellingPrice", form.sellingPrice || 0);

    appendText("discountedPrice", form.discountedPrice || 0);

    appendText("taxPercentage", form.taxPercentage || 0);

    /* ===================================================
         INVENTORY
      =================================================== */

    /*
     * IMPORTANT:
     *
     * Create:
     * sends opening stock.
     *
     * Edit:
     * does NOT send stockQty because backend
     * correctly requires stock adjustments
     * through Stock Movement API.
     */

    if (!isEdit) {
      appendText("stockQty", form.stockQty || 0);
    }

    appendText("reservedQty", form.reservedQty || 0);

    appendText("lowStockThreshold", form.lowStockThreshold || 0);

    appendText("unit", form.unit);

    /* ===================================================
         GENERAL SPECIFICATIONS
      =================================================== */

    appendText("color", form.color);

    appendText("size", form.size);

    appendText("warranty", form.warranty);

    /* ===================================================
         SUPPLIER
      =================================================== */

    appendText("supplierName", form.supplierName);

    appendText("supplierMobile", form.supplierMobile);

    appendText("supplierInvoiceNumber", form.supplierInvoiceNumber);

    /* ===================================================
         ATTRIBUTES
      =================================================== */

    formData.append(
      "attributes",

      JSON.stringify(getNormalizedAttributes()),
    );

    /* ===================================================
         TAGS
      =================================================== */

    formData.append(
      "tags",

      JSON.stringify(commaStringToArray(form.tagsText)),
    );

    /* ===================================================
         SEO
      =================================================== */

    appendText("metaTitle", form.metaTitle);

    appendText("metaDescription", form.metaDescription);

    formData.append(
      "metaKeywords",

      JSON.stringify(commaStringToArray(form.metaKeywordsText)),
    );

    /* ===================================================
         STATUS
      =================================================== */

    appendText("status", form.status);

    appendText("isFeatured", Boolean(form.isFeatured));

    appendText("isVerified", Boolean(form.isVerified));

    appendText("dealEnd", form.dealEnd);

    /* ===================================================
         EXISTING IMAGES

         Update only.
      =================================================== */

    if (isEdit) {
      formData.append(
        "existingImages",

        JSON.stringify(
          imagePreview.filter(
            (image) => typeof image === "string" && !image.startsWith("blob:"),
          ),
        ),
      );
    }

    /* ===================================================
         NEW THUMBNAIL
      =================================================== */

    if (thumbnail) {
      formData.append("thumbnail", thumbnail);
    }

    /* ===================================================
         NEW GALLERY FILES
      =================================================== */

    images.forEach((image) => {
      formData.append("images", image);
    });

    return formData;
  };

  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    if (!validate()) {
      const firstError = Object.values(errors)[0];

      if (firstError) {
        await swal.warning({
          icon: "warning",

          title: "Check Product Details",

          text: firstError,
        });
      }

      return;
    }

    try {
      setLoading(true);

      const formData = buildFormData();

      const response = isEdit
        ? await api.postFormData(
            config.updateInventoryProduct,

            formData,
          )
        : await api.postFormData(
            config.createInventoryProduct,

            formData,
          );

      if (response?.status !== "success" && response?.success !== true) {
        throw new Error(response?.message || "Unable to save product.");
      }

      await swal.success({
        icon: "success",

        title: isEdit ? "Product Updated" : "Product Created",

        text:
          response?.message ||
          (isEdit
            ? "Product updated successfully."
            : "Product created successfully."),

        timer: 1500,

        showConfirmButton: false,
      });

      if (typeof onSuccess === "function") {
        onSuccess(response?.data || null);
      }
    } catch (requestError) {
      console.error("Save product error:", requestError);

      await swal.error({
        icon: "error",

        title: isEdit ? "Unable to Update Product" : "Unable to Create Product",

        text:
          requestError?.response?.data?.message ||
          requestError?.message ||
          "Unable to save product.",
      });
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose = () => {
    if (loading) {
      return;
    }

    onClose?.();
  };

  /* =======================================================
     DISCOUNT PREVIEW
  ======================================================= */

  const discountPercentage = useMemo(() => {
    const selling = Number(form.sellingPrice || 0);

    const discounted = Number(form.discountedPrice || 0);

    if (selling <= 0 || discounted <= 0 || discounted >= selling) {
      return 0;
    }

    return Math.round(((selling - discounted) / selling) * 100);
  }, [form.sellingPrice, form.discountedPrice]);

  /* =======================================================
     PROFIT PREVIEW
  ======================================================= */

  const profitPerUnit = useMemo(() => {
    const purchase = Number(form.purchasePrice || 0);

    const selling = Number(form.sellingPrice || 0);

    const discounted = Number(form.discountedPrice || 0);

    const effectivePrice =
      discounted > 0 && discounted < selling ? discounted : selling;

    return effectivePrice - purchase;
  }, [form.purchasePrice, form.sellingPrice, form.discountedPrice]);

  /* =======================================================
     DON'T RENDER
  ======================================================= */

  if (!open) {
    return null;
  }

  return (
    <div className="inventory-product-modal-overlay">
      <div
        className="inventory-product-modal"
        role="dialog"
        aria-modal="true"
        aria-label={isEdit ? "Update Product" : "Add New Product"}
      >
        <div className="inventory-product-modal-header">
          <div>
            <span>{isEdit ? "EDIT PRODUCT" : "NEW PRODUCT"}</span>
            <h2>{isEdit ? "Update Product" : "Add New Product"}</h2>
            <p>
              Manage product information, pricing, inventory, images,
              specifications and storefront settings.
            </p>
          </div>

          <button
            type="button"
            className="inventory-product-modal-close"
            onClick={onClose}
            disabled={loading}
            aria-label="Close product form"
          >
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} autoComplete="off">
          <div className="inventory-product-modal-body">
            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiPackage />
                <div>
                  <strong>Basic Information</strong>
                  <span>Product identity and catalog information</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-3">
                <div className="inventory-product-form-group inventory-product-form-full">
                  <label>
                    Product Name <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={form.name}
                    onChange={handleChange}
                    placeholder="Example: Dell Latitude 5420"
                    disabled={loading}
                  />
                  {errors.name ? (
                    <small className="inventory-product-field-error">
                      {errors.name}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>
                    SKU <span>*</span>
                  </label>
                  <div className="inventory-product-input-action">
                    <input
                      type="text"
                      name="sku"
                      value={form.sku}
                      onChange={handleChange}
                      placeholder="CW-000001"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((previous) => ({
                          ...previous,
                          sku: generateSKU(),
                        }))
                      }
                      disabled={loading}
                    >
                      Generate
                    </button>
                  </div>
                  {errors.sku ? (
                    <small className="inventory-product-field-error">
                      {errors.sku}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Barcode</label>
                  <div className="inventory-product-input-action">
                    <input
                      type="text"
                      name="barcode"
                      value={form.barcode}
                      onChange={handleChange}
                      placeholder="Barcode / EAN"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setForm((previous) => ({
                          ...previous,
                          barcode: generateBarcode(),
                        }))
                      }
                      disabled={loading}
                    >
                      Generate
                    </button>
                  </div>
                </div>

                <div className="inventory-product-form-group">
                  <label>
                    Slug <span>*</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={form.slug}
                    onChange={handleChange}
                    placeholder="dell-latitude-5420"
                    disabled={loading}
                  />
                  {errors.slug ? (
                    <small className="inventory-product-field-error">
                      {errors.slug}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Brand</label>
                  <input
                    type="text"
                    name="brand"
                    value={form.brand}
                    onChange={handleChange}
                    placeholder="Dell"
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Model Number</label>
                  <input
                    type="text"
                    name="modelNumber"
                    value={form.modelNumber}
                    onChange={handleChange}
                    placeholder="Latitude 5420"
                    disabled={loading}
                  />
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiTag />
                <div>
                  <strong>Category & Classification</strong>
                  <span>Organize the product for inventory and storefront</span>
                </div>
              </div>

              <div className="inventory-product-classification-grid">
                <div className="inventory-product-category-select-wrapper">
                  <CategorySelect
                    category={form.category}
                    subCategory={form.subCategory}
                    required
                    disabled={loading}
                    categoryError={errors.category}
                    subCategoryError={errors.subCategory}
                    onCategoryChange={handleCategoryChange}
                    onSubCategoryChange={handleSubCategoryChange}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Product Type</label>
                  <select
                    name="productType"
                    value={form.productType}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {PRODUCT_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="inventory-product-form-group">
                  <label>Condition</label>
                  <select
                    name="condition"
                    value={form.condition}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {PRODUCT_CONDITIONS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiDollarSign />
                <div>
                  <strong>Pricing</strong>
                  <span>Purchase cost, selling price, discount and tax</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-4">
                <div className="inventory-product-form-group">
                  <label>Purchase Price</label>
                  <input
                    type="number"
                    name="purchasePrice"
                    min="0"
                    step="0.01"
                    value={form.purchasePrice}
                    onChange={handleChange}
                    placeholder="0"
                    disabled={loading}
                  />
                  {errors.purchasePrice ? (
                    <small className="inventory-product-field-error">
                      {errors.purchasePrice}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>
                    Selling Price <span>*</span>
                  </label>
                  <input
                    type="number"
                    name="sellingPrice"
                    min="0"
                    step="0.01"
                    value={form.sellingPrice}
                    onChange={handleChange}
                    placeholder="0"
                    disabled={loading}
                  />
                  {errors.sellingPrice ? (
                    <small className="inventory-product-field-error">
                      {errors.sellingPrice}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Discounted Price</label>
                  <input
                    type="number"
                    name="discountedPrice"
                    min="0"
                    step="0.01"
                    value={form.discountedPrice}
                    onChange={handleChange}
                    placeholder="0"
                    disabled={loading}
                  />
                  {errors.discountedPrice ? (
                    <small className="inventory-product-field-error">
                      {errors.discountedPrice}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Tax %</label>
                  <input
                    type="number"
                    name="taxPercentage"
                    min="0"
                    max="100"
                    step="0.01"
                    value={form.taxPercentage}
                    onChange={handleChange}
                    placeholder="0"
                    disabled={loading}
                  />
                  {errors.taxPercentage ? (
                    <small className="inventory-product-field-error">
                      {errors.taxPercentage}
                    </small>
                  ) : null}
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiBox />
                <div>
                  <strong>Inventory</strong>
                  <span>
                    Opening stock, reserved quantity, alert threshold and unit
                  </span>
                </div>
              </div>

              {isEdit ? (
                <div className="inventory-product-stock-edit-note">
                  <FiBox />
                  <div>
                    <strong>Stock is movement controlled</strong>
                    <span>
                      Use Stock Adjustment to change existing stock so movement
                      history remains accurate.
                    </span>
                  </div>
                </div>
              ) : null}

              <div className="inventory-product-form-grid-4">
                <div className="inventory-product-form-group">
                  <label>{isEdit ? "Current Stock" : "Opening Stock"}</label>
                  <input
                    type="number"
                    name="stockQty"
                    min="0"
                    step="1"
                    value={form.stockQty}
                    onChange={handleChange}
                    disabled={loading || isEdit}
                    placeholder="0"
                  />
                  {!isEdit ? (
                    <small>
                      Opening stock movement is created automatically.
                    </small>
                  ) : null}
                  {errors.stockQty ? (
                    <small className="inventory-product-field-error">
                      {errors.stockQty}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Reserved Quantity</label>
                  <input
                    type="number"
                    value={form.reservedQty || 0}
                    disabled
                  />
                  {errors.reservedQty ? (
                    <small className="inventory-product-field-error">
                      {errors.reservedQty}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Low Stock Alert</label>
                  <input
                    type="number"
                    name="lowStockThreshold"
                    min="0"
                    step="1"
                    value={form.lowStockThreshold}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  {errors.lowStockThreshold ? (
                    <small className="inventory-product-field-error">
                      {errors.lowStockThreshold}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-form-group">
                  <label>Unit</label>
                  <select
                    name="unit"
                    value={form.unit}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {PRODUCT_UNITS.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="inventory-product-form-group">
                  <label>Warranty</label>
                  <input
                    type="text"
                    name="warranty"
                    value={form.warranty}
                    onChange={handleChange}
                    placeholder="Example: 6 Months"
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Color</label>
                  <input
                    type="text"
                    name="color"
                    value={form.color}
                    onChange={handleChange}
                    placeholder="Example: Black"
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Size</label>
                  <input
                    type="text"
                    name="size"
                    value={form.size}
                    onChange={handleChange}
                    placeholder="Product size"
                    disabled={loading}
                  />
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <DynamicAttributes
                attributes={form.attributes || []}
                disabled={loading}
                onChange={handleAttributesChange}
              />
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiTruck />
                <div>
                  <strong>Supplier Information</strong>
                  <span>Optional purchase and supplier reference information</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-3">
                <div className="inventory-product-form-group">
                  <label>Supplier Name</label>
                  <input
                    type="text"
                    name="supplierName"
                    value={form.supplierName}
                    onChange={handleChange}
                    placeholder="Supplier name"
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Supplier Mobile</label>
                  <input
                    type="text"
                    name="supplierMobile"
                    value={form.supplierMobile}
                    onChange={handleChange}
                    placeholder="Mobile number"
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Purchase Invoice Number</label>
                  <input
                    type="text"
                    name="supplierInvoiceNumber"
                    value={form.supplierInvoiceNumber}
                    onChange={handleChange}
                    placeholder="INV-001"
                    disabled={loading}
                  />
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiFileText />
                <div>
                  <strong>Product Description</strong>
                  <span>Storefront short and detailed descriptions</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-2">
                <div className="inventory-product-form-group">
                  <label>Short Description</label>
                  <textarea
                    name="shortDescription"
                    rows={4}
                    value={form.shortDescription}
                    onChange={handleChange}
                    placeholder="Short product summary..."
                    maxLength={500}
                    disabled={loading}
                  />
                  <span className="inventory-product-character-count">
                    {String(form.shortDescription || "").length}/500
                  </span>
                </div>

                <div className="inventory-product-form-group">
                  <label>Full Description</label>
                  <textarea
                    name="description"
                    rows={4}
                    value={form.description}
                    onChange={handleChange}
                    placeholder="Complete product description..."
                    maxLength={10000}
                    disabled={loading}
                  />
                  <span className="inventory-product-character-count">
                    {String(form.description || "").length}/10000
                  </span>
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiImage />
                <div>
                  <strong>Product Images</strong>
                  <span>Upload a thumbnail and up to five gallery images</span>
                </div>
              </div>

              <div className="inventory-product-image-sections">
                <div className="inventory-product-thumbnail-section">
                  <div className="inventory-product-upload-heading">
                    <div>
                      <strong>Thumbnail</strong>
                      <span>Main image shown in product listings</span>
                    </div>

                    <label className="inventory-product-upload-button">
                      <FiUpload />
                      Choose Image
                      <input
                        type="file"
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleThumbnail}
                        disabled={loading}
                      />
                    </label>
                  </div>

                  {thumbnailPreview ? (
                    <div className="inventory-product-thumbnail-preview">
                      <img
                        src={getImageUrl(thumbnailPreview)}
                        alt="Product thumbnail"
                      />
                      <button
                        type="button"
                        onClick={removeThumbnail}
                        disabled={loading}
                        aria-label="Remove thumbnail"
                      >
                        <FiTrash2 />
                      </button>
                    </div>
                  ) : (
                    <div className="inventory-product-image-empty">
                      <FiImage />
                      <span>No thumbnail selected</span>
                    </div>
                  )}

                  {errors.thumbnail ? (
                    <small className="inventory-product-field-error">
                      {errors.thumbnail}
                    </small>
                  ) : null}
                </div>

                <div className="inventory-product-gallery-section">
                  <div className="inventory-product-upload-heading">
                    <div>
                      <strong>Product Gallery</strong>
                      <span>Maximum {MAX_PRODUCT_IMAGES} images</span>
                    </div>

                    <label className="inventory-product-upload-button">
                      <FiUpload />
                      Add Images
                      <input
                        type="file"
                        multiple
                        accept="image/jpeg,image/jpg,image/png,image/webp"
                        onChange={handleImages}
                        disabled={
                          loading || imagePreview.length >= MAX_PRODUCT_IMAGES
                        }
                      />
                    </label>
                  </div>

                  {imagePreview.length ? (
                    <div className="inventory-product-gallery-grid">
                      {imagePreview.map((image, index) => (
                        <div
                          key={`${image}-${index}`}
                          className="inventory-product-gallery-item"
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`Product ${index + 1}`}
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            disabled={loading}
                            aria-label={`Remove image ${index + 1}`}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="inventory-product-image-empty">
                      <FiImage />
                      <span>No gallery images selected</span>
                    </div>
                  )}

                  <div className="inventory-product-image-count">
                    {imagePreview.length}/{MAX_PRODUCT_IMAGES} images
                  </div>
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiShoppingBag />
                <div>
                  <strong>Search & Storefront</strong>
                  <span>Tags, deal timing and product discoverability</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-2">
                <div className="inventory-product-form-group">
                  <label>Tags</label>
                  <input
                    type="text"
                    name="tagsText"
                    value={form.tagsText}
                    onChange={handleChange}
                    placeholder="laptop, dell, i5, business"
                    disabled={loading}
                  />
                  <small>Separate tags using commas.</small>
                </div>

                <div className="inventory-product-form-group">
                  <label>Deal End</label>
                  <input
                    type="date"
                    name="dealEnd"
                    value={form.dealEnd}
                    onChange={handleChange}
                    disabled={loading}
                  />
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiSettings />
                <div>
                  <strong>SEO</strong>
                  <span>Search-engine metadata for the storefront</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-2">
                <div className="inventory-product-form-group">
                  <label>Meta Title</label>
                  <input
                    type="text"
                    name="metaTitle"
                    value={form.metaTitle}
                    onChange={handleChange}
                    placeholder="SEO title"
                    maxLength={160}
                    disabled={loading}
                  />
                </div>

                <div className="inventory-product-form-group">
                  <label>Meta Keywords</label>
                  <input
                    type="text"
                    name="metaKeywordsText"
                    value={form.metaKeywordsText}
                    onChange={handleChange}
                    placeholder="dell, laptop, latitude"
                    disabled={loading}
                  />
                  <small>Separate keywords using commas.</small>
                </div>

                <div className="inventory-product-form-group inventory-product-form-full">
                  <label>Meta Description</label>
                  <textarea
                    name="metaDescription"
                    rows={4}
                    value={form.metaDescription}
                    onChange={handleChange}
                    placeholder="Write a concise SEO description..."
                    maxLength={320}
                    disabled={loading}
                  />
                  <span className="inventory-product-character-count">
                    {String(form.metaDescription || "").length}/320
                  </span>
                </div>
              </div>
            </section>

            <section className="inventory-product-form-card">
              <div className="inventory-product-form-card-title">
                <FiSettings />
                <div>
                  <strong>Publishing & Storefront</strong>
                  <span>Control product availability and storefront visibility</span>
                </div>
              </div>

              <div className="inventory-product-form-grid-3">
                <div className="inventory-product-form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={form.status}
                    onChange={handleChange}
                    disabled={loading}
                  >
                    {PRODUCT_STATUSES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="inventory-product-switch-grid">
                <label>
                  <input
                    type="checkbox"
                    name="isFeatured"
                    checked={Boolean(form.isFeatured)}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span>
                    <strong>Featured Product</strong>
                    Show product in featured product sections.
                  </span>
                </label>

                <label>
                  <input
                    type="checkbox"
                    name="isVerified"
                    checked={Boolean(form.isVerified)}
                    onChange={handleChange}
                    disabled={loading}
                  />
                  <span>
                    <strong>Verified Product</strong>
                    Mark product information as reviewed.
                  </span>
                </label>
              </div>
            </section>
          </div>

          <div className="inventory-product-modal-footer">
            <button
              type="button"
              className="inventory-product-cancel-button"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="inventory-product-save-button"
              disabled={loading}
            >
              <FiSave />
              {loading
                ? "Saving..."
                : isEdit
                  ? "Update Product"
                  : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddEditProduct;